/*
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 UNESP Universidade Estadual Paulista "Júlio de Mesquita Filho"
 * Copyright 2024-2026 UNICAMP Universidade Estadual de Campinas
 *
 */

export enum TipoAndamento {
  ABERTURA = 'Abertura de Inscrições',
  PRORROGACAO_INSCRICOES = 'Prorrogação das Inscrições',
  CANCELADO = 'Cancelado',
  COMUNICADO = 'Comunicado',
  CONVOCACAO = 'Convocação para as Provas',
  DEFERIMENTO_INSCRICOES = 'Analise das Inscrições',
  LISTA_DEFINITIVA_INSCRITOS = 'Lista Definitiva de Inscritos e Abertura de Prazo para Impugnação de Banca  ',
  GABARITO = 'Divulgação do Gabarito e Caderno de Questões',
  HOMOLOGACAO = 'Homologação',
  IMPUGNACAO_COMISSAO = 'Divulgação da Comissão Examinadora',
  REDUCAO = 'Resultado da Redução de Taxa',
  RESULTADO_FASE_1 = 'Resultado da Fase 1',
  RESULTADO_FASE_2 = 'Resultado da Fase 2',
  RESULTADO_FINAL = 'Resultado Final Provisório',
  RESULTADO_FINAL_DEFINITIVO = 'Resultado Final Definitivo',
  RESULTADO_RECURSO = 'Resultado de Análise de Recurso',
  RETIFICACAO = 'Retificação de Edital',
  REVOGADO = 'Edital de Revogação',
  CONVOCACAO_ANUENCIA = 'Convocação para Anuência do Candidato',
  ANALISE_INSCRICOES_PPI = 'Análise das Inscrições como PPI',
  RESULTADO_BANCA_PPI = 'Resultado de Análise de Banca de Heteroidentificação',
  RESULTADO_DOC_INDIGENAS = 'Resultado de Análise de Documentos - Indígena',
}

export function getAndamentoByDescription(value: string) {
  const indexOfS = Object.values(TipoAndamento).indexOf(value as unknown as TipoAndamento)
  const key = Object.keys(TipoAndamento)[indexOfS]
  return key
}

export interface TipoAndamentoDetalhe {
  recurso: boolean
  doe: boolean
  prazo: number
  anexoObrigatorio: boolean
}

export const TipoAndamentoDetalhes = new Map<string, TipoAndamentoDetalhe>([
  ['ABERTURA', { recurso: false, doe: true, prazo: 0, anexoObrigatorio: true }],
  ['PRORROGACAO_INSCRICOES', { recurso: false, doe: true, prazo: 0, anexoObrigatorio: false }],
  ['CANCELADO', { recurso: false, doe: true, prazo: 0, anexoObrigatorio: false }],
  ['COMUNICADO', { recurso: false, doe: true, prazo: 0, anexoObrigatorio: false }],
  ['CONVOCACAO', { recurso: false, doe: true, prazo: 0, anexoObrigatorio: false }],
  ['DEFERIMENTO_INSCRICOES', { recurso: true, doe: true, prazo: 5, anexoObrigatorio: true }],
  ['LISTA_DEFINITIVA_INSCRITOS', { recurso: true, doe: true, prazo: 2, anexoObrigatorio: true }],
  ['GABARITO', { recurso: true, doe: true, prazo: 3, anexoObrigatorio: true }],
  ['HOMOLOGACAO', { recurso: false, doe: true, prazo: 0, anexoObrigatorio: false }],
  ['IMPUGNACAO_COMISSAO', { recurso: true, doe: true, prazo: 2, anexoObrigatorio: true }],
  ['REDUCAO', { recurso: true, doe: false, prazo: 3, anexoObrigatorio: false }],
  ['RESULTADO_FASE_1', { recurso: true, doe: true, prazo: 2, anexoObrigatorio: true }],
  ['RESULTADO_FASE_2', { recurso: true, doe: true, prazo: 2, anexoObrigatorio: true }],
  ['RESULTADO_FINAL', { recurso: true, doe: true, prazo: 5, anexoObrigatorio: true }],
  ['RESULTADO_FINAL_DEFINITIVO', { recurso: false, doe: true, prazo: 0, anexoObrigatorio: true }],
  ['RESULTADO_RECURSO', { recurso: false, doe: true, prazo: 0, anexoObrigatorio: false }],
  ['RETIFICACAO', { recurso: false, doe: true, prazo: 0, anexoObrigatorio: true }],
  ['REVOGADO', { recurso: false, doe: true, prazo: 0, anexoObrigatorio: true }],
  ['CONVOCACAO_ANUENCIA', { recurso: false, doe: true, prazo: 0, anexoObrigatorio: false }],
  ['ANALISE_INSCRICOES_PPI', { recurso: true, doe: true, prazo: 3, anexoObrigatorio: true }],
  ['RESULTADO_BANCA_PPI', { recurso: true, doe: true, prazo: 3, anexoObrigatorio: true }],
  ['RESULTADO_DOC_INDIGENAS', { recurso: true, doe: true, prazo: 3, anexoObrigatorio: true }],
])

export const AndamentosPorTipoConcurso = new Map<string, string[]>([
  ['PT', [
    'ABERTURA', 'PRORROGACAO_INSCRICOES', 'CANCELADO', 'COMUNICADO',
    'CONVOCACAO', 'DEFERIMENTO_INSCRICOES', 'LISTA_DEFINITIVA_INSCRITOS',
    'GABARITO', 'HOMOLOGACAO', 'IMPUGNACAO_COMISSAO', 'REDUCAO', 'RESULTADO_FASE_1',
    'RESULTADO_FINAL', 'RESULTADO_FINAL_DEFINITIVO',
    'RESULTADO_RECURSO', 'RETIFICACAO', 'REVOGADO', 'CONVOCACAO_ANUENCIA'
  ]],
  ['PTPP', [
    'ABERTURA', 'PRORROGACAO_INSCRICOES', 'CANCELADO', 'COMUNICADO',
    'CONVOCACAO', 'DEFERIMENTO_INSCRICOES', 'LISTA_DEFINITIVA_INSCRITOS',
    'GABARITO', 'HOMOLOGACAO', 'IMPUGNACAO_COMISSAO', 'REDUCAO', 'RESULTADO_FASE_1',
    'RESULTADO_FINAL', 'RESULTADO_FINAL_DEFINITIVO',
    'RESULTADO_RECURSO', 'RETIFICACAO', 'REVOGADO', 'CONVOCACAO_ANUENCIA'
  ]]
])

const ANDAMENTOS_PADRAO = [
  'ABERTURA', 'PRORROGACAO_INSCRICOES', 'CANCELADO', 'COMUNICADO',
  'CONVOCACAO', 'DEFERIMENTO_INSCRICOES', 'LISTA_DEFINITIVA_INSCRITOS',
  'GABARITO', 'HOMOLOGACAO', 'IMPUGNACAO_COMISSAO', 'REDUCAO',
  'RESULTADO_FASE_1', 'RESULTADO_FINAL',
  'RESULTADO_FINAL_DEFINITIVO', 'RESULTADO_RECURSO', 'RETIFICACAO',
  'REVOGADO', 'CONVOCACAO_ANUENCIA', 'ANALISE_INSCRICOES_PPI',
  'RESULTADO_BANCA_PPI', 'RESULTADO_DOC_INDIGENAS'
]

export function getAndamentosPorTipoConcurso(tipoConcurso: string): string[] {
  return AndamentosPorTipoConcurso.get(tipoConcurso) || ANDAMENTOS_PADRAO
}
